import { Box, Button, Link, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  applyStoredConsent,
  getStoredConsent,
  isCookieConsentPending,
  updateConsent,
  type CookieConsentValue,
} from "../analytics/gtm";

type CookieConsentProps = {
  /** Optional privacy policy URL; omit to hide the link. */
  privacyPolicyUrl?: string;
};

export default function CookieConsent({ privacyPolicyUrl }: CookieConsentProps) {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(isCookieConsentPending() && getStoredConsent() == null);
  }, [location.pathname]);

  function choose(value: CookieConsentValue) {
    updateConsent(value === "granted");
    setVisible(false);
  }

  // Ensure returning visitors with a stored choice have Consent Mode updated
  // even if AnalyticsProvider already applied it (idempotent).
  useEffect(() => {
    applyStoredConsent();
  }, []);

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: { xs: 8, sm: 16 },
        px: { xs: 1, sm: 2 },
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={8}
        role="dialog"
        aria-label="Cookie consent"
        sx={{
          p: 2,
          width: "100%",
          maxWidth: 720,
          boxSizing: "border-box",
          pointerEvents: "auto",
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2">
            We use analytics cookies to understand how School Happy is used so we can
            improve it. Tracking is on by default; you can reject if you prefer.
            {privacyPolicyUrl ? (
              <>
                {" "}
                See our{" "}
                <Link href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                  privacy policy
                </Link>
                .
              </>
            ) : null}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button variant="outlined" size="small" onClick={() => choose("denied")}>
              Reject
            </Button>
            <Button variant="contained" size="small" onClick={() => choose("granted")}>
              OK
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
